import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Spin, Input, Button, message } from "antd";

// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faEnvelope, faLock } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch } from "react-redux";
import {
  API_BASE_URL_KEY,
  LOGIN_OFFLINE_URL,
  OFFLINE_INITIAL_PASSWORD_URL,
  OWN_CUSTOM_OFFLINE_PASSWORD_URL,
  apiBaseUrl,
  loginAttempts,
  usePostLoginOfflineMutation,
  usePostOfflineInitialPasswordMutation,
  usePostOwnCustomOfflinePasswordMutation,
} from "../../redux/login/loginAPI";
import { updateUser } from "../../redux/login/loginSlice";
import { storeOfflineSessionToken } from "../../redux/authentication/authentication";
import { DEFAULT_APP_VERSION } from "../../constants/login";
// CUSTOM AMPLIFY COMPONENTS
import CustomAmplifyCarousel from "../login/CustomAmplifyCarousel";
import { isConnectionFailure, probeServerReachability } from "./connectionDiagnosis";
import { LoginErrorMessage, describeLoginError } from "./loginErrorMessage";
import { downloadLoginLogBundle, readBrowserEnvironment } from "./logBundle";
// STYLES
import "../../styles/generalStyles.scss";
// UTILS
import { emptyFunctionDeclaration } from "../../utils/utils";

// Mirrors backend passwordMinLength + complexity rules. Server is authoritative;
// these are pre-checks for fast UX feedback only.
const PASSWORD_MIN_LENGTH = 12;

function passwordComplexityError(pw: string): string | null {
  if (pw.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
  }
  const hasUpper = /[A-Z]/.test(pw);
  const hasLower = /[a-z]/.test(pw);
  const hasDigit = /[0-9]/.test(pw);
  if (!hasUpper || !hasLower || !hasDigit) {
    return "Password must include an uppercase letter, a lowercase letter, and a digit";
  }
  return null;
}

const joinUrl = (baseUrl: string, path: string) => {
  const sanitizedBase = (baseUrl || "").replace(/\/+$/, "");
  const sanitizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${sanitizedBase}${sanitizedPath}`;
};

// The request error a screen is showing: what failed, where, and which of the page's flows it was.
type Failure = { error: unknown; path: string; flow: string };

const LoginOfflinePage = () => {
  // ************************************************ */
  // LOCAL STORAGE AND VARIABLES ******************** */
  const dispatch = useDispatch();
  const [launchToDashboard, setLaunchToDashboard] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [failedAt, setFailedAt] = useState<Date | null>(null);
  const [isPreparingLog, setIsPreparingLog] = useState(false);

  // First-login set-password state
  const [setupRequired, setSetupRequired] = useState(false);
  const [setupNewPassword, setSetupNewPassword] = useState("");
  const [setupConfirmPassword, setSetupConfirmPassword] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [setupClientError, setSetupClientError] = useState("");

  // Forced-change state. Distinct from the setup flow above: this user HAS a
  // password (the one the server generated and an administrator read out to
  // them) and has just authenticated with it. The server refuses every other
  // route until it is replaced, so there is nothing to navigate to and no
  // reason to offer a skip.
  const [forcedChangeToken, setForcedChangeToken] = useState("");

  // ************************************************ */
  // FUNCTIONS ************************************** */

  const [triggerLogin, { data, isLoading, isError, error }] = usePostLoginOfflineMutation();
  const [
    triggerSetInitialPassword,
    { data: setupData, isLoading: setupIsLoading, isError: setupIsError, error: setupError },
  ] = usePostOfflineInitialPasswordMutation();
  const [
    triggerChangeOwnPassword,
    { data: changeData, isLoading: changeIsLoading, isError: changeIsError, error: changeError },
  ] = usePostOwnCustomOfflinePasswordMutation();

  const onClickLogin = () => {
    triggerLogin({ body: { email, password } });
  };

  const onClickSetInitialPassword = () => {
    setSetupClientError("");
    if (!setupToken.trim()) {
      setSetupClientError("Setup token is required (ask your administrator)");
      return;
    }
    const complexityError = passwordComplexityError(setupNewPassword);
    if (complexityError) {
      setSetupClientError(complexityError);
      return;
    }
    if (setupNewPassword !== setupConfirmPassword) {
      setSetupClientError("Passwords do not match");
      return;
    }
    triggerSetInitialPassword({
      body: {
        email,
        password: setupNewPassword,
        setup_token: setupToken.trim(),
      },
    });
  };

  const onClickChangeOwnPassword = () => {
    setSetupClientError("");
    const complexityError = passwordComplexityError(setupNewPassword);
    if (complexityError) {
      setSetupClientError(complexityError);
      return;
    }
    if (setupNewPassword !== setupConfirmPassword) {
      setSetupClientError("Passwords do not match");
      return;
    }
    triggerChangeOwnPassword({
      // `Bearer` is the caller's job everywhere in this codebase: the token is
      // stored raw and the prefix is added at the call (see
      // refreshOfflineToken in redux/authentication). Sending it bare made the
      // JWT middleware answer "You are not logged in", which reads as a session
      // problem rather than a malformed header -- and it stranded the user on
      // the one screen they must pass, since requirePasswordChange sends an
      // OFFLINE_CREATED account here and nowhere else.
      token: `Bearer ${forcedChangeToken}`,
      body: { new_password: setupNewPassword },
    });
  };

  // ************************************************ */
  // HELPERS *************************************** */
  const completeAuthentication = (token: string) => {
    // Also drops any refresh token left by an earlier session (LIFT-1085).
    storeOfflineSessionToken(token);
    const response = {
      username: "5c099235-165e-43d6-b0ba-8a8581a1ab17",
      pool: {
        userPoolId: "us-east-2_G7uEE7P0C",
        clientId: "s9en4iv123b6qlrugg0cfgioh",
        client: {
          endpoint: "https://cognito-idp.us-east-2.amazonaws.com/",
          fetchOptions: {},
        },
        advancedSecurityDataCollectionFlag: true,
        storage: {
          headerTitle: "monitoring",
          useLoggedAmplify: "false",
          theme: "blue",
        },
      },
      Session:
        "AYABeCmDmgDN-EVJ01yFyAsIp-EAHQABAAdTZXJ2aWNlABBDb2duaXRvVXNlclBvb2xzAAEAB2F3cy1rbXMAS2Fybjphd3M6a21zOnVzLWVhc3QtMjo0MTc1Njc5MDM0Njk6a2V5LzVjZDI0ZDRjLWVjNWItNGU4Ny05MGI2LTVkODdkOTZmY2RkMgC4AQIBAHjif3k0w30uAyP92ifoZ0jN6g50UW_KR0w9Vv2c_wlQAgH-QZ-NNGghuF7hjt1FrqQ1AAAAfjB8BgkqhkiG9w0BBwagbzBtAgEAMGgGCSqGSIb3DQEHATAeBglghkgBZQMEAS4wEQQMfbxqDNdgRVj2ZWcOAgEQgDvTZFVcFmETCaO2hHNl1P1ZeF1M8ItTtf04bTQYaVoIEanOb-byKKK1cD1YpfJzlLFKh-oaA3G7OQ04wQIAAAAADAAAEAAAAAAAAAAAAAAAAADKqQVsVLTOhTHklfBxkobW_____wAAAAEAAAAAAAAAAAAAAAEAAAEASfR7LCnePUtBuptfuzwMxPPqrMcftN4aZy3X0u_etosge8p-qKs4BGJ3-dgn9HvHKLSQYID6xDTPzaQKzwDFmyJcMyL6o6Lm8MVal36tRGx3unrguUjGpaSmWhPYyuWpT7e9tzdqq3Ny_p_DRwxKV15yl3WtygelcJqpg5ORCR1aenDrpvXFpYTDy_tSTK6g6Hb_AigqZShZo7Et25WM2UsWvUvKDHb0pgFSTbBV7cqrpKWbHkXOTW6gfTEI1WP423MPlZl5t-daU0S0JSP44Lm0A0WhCBuT4H4XJtNDyTVbsBmtM9j5HRmva7lDWKIZwWy2HKChyVFL6gVPZj4SC9WmLEc4JlzzLIgnK83OIoU",
      client: {
        endpoint: "https://cognito-idp.us-east-2.amazonaws.com/",
        fetchOptions: {},
      },
      signInUserSession: null,
      authenticationFlowType: "USER_SRP_AUTH",
      storage: {
        headerTitle: "monitoring",
        useLoggedAmplify: "false",
        theme: "blue",
      },
      keyPrefix: "CognitoIdentityServiceProvider.s9en4iv123b6qlrugg0cfgioh",
      userDataKey:
        "CognitoIdentityServiceProvider.s9en4iv123b6qlrugg0cfgioh.5c099235-165e-43d6-b0ba-8a8581a1ab17.userData",
      challengeName: "SOFTWARE_TOKEN_MFA",
      challengeParam: { FRIENDLY_DEVICE_NAME: "My TOTP device" },
    };
    localStorage.setItem("useLoggedAmplify", "true");
    dispatch(updateUser(response));
    setLaunchToDashboard(true);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (data?.password_setup_required) {
      // Server told us this account has no password yet — switch to setup UI.
      setSetupRequired(true);
      setSetupClientError("");
      setPassword("");
      return;
    }
    if (data?.token) {
      // The login response carries the account's status. OFFLINE_CREATED means
      // the password just used is the one the SERVER generated, so hold the
      // token for the change call and show the change screen instead of
      // entering the app. The server enforces this too -- it refuses every
      // other route with PASSWORD_CHANGE_REQUIRED -- so skipping the screen
      // would only produce a session that cannot do anything.
      if (String(data?.status ?? "").toUpperCase() === "OFFLINE_CREATED") {
        setForcedChangeToken(data.token);
        setSetupClientError("");
        setPassword("");
        return;
      }
      completeAuthentication(data.token);
    }
  }, [data]);

  useEffect(() => {
    if (changeData) {
      // The account is now OFFLINE_ACTIVE and the gate has lifted. Reuse the
      // token the login issued rather than asking for the new password again.
      completeAuthentication(forcedChangeToken);
    }
  }, [changeData]);

  useEffect(() => {
    if (setupData?.token) {
      // First-login set-password succeeded — auto-login with the issued JWT.
      completeAuthentication(setupData.token);
    }
  }, [setupData]);

  useEffect(() => {
    if (error) {
      setEmail("");
      setPassword("");
    }
  }, [error, isError]);

  // When the newest request error appeared, for the support log: a log fetched minutes later must
  // still say when the failure happened.
  useEffect(() => {
    if (error || setupError || changeError) {
      setFailedAt(new Date());
    }
  }, [error, setupError, changeError]);

  // ************************************************ */
  // DERIVED *************************************** */
  // Two different reasons land on the same "choose a password" panel, and they
  // are NOT the same flow: setup is for an account with no password at all and
  // needs a setup token, forced change is for one holding a server-generated
  // password and needs the token the login just issued. Kept as separate flags
  // rather than one, so the panel can differ where it must.
  const forcedChangeRequired = forcedChangeToken !== "";
  const showPasswordPanel = setupRequired || forcedChangeRequired;

  const panelTitle = () => {
    if (forcedChangeRequired) return "Choose Your Own Password";
    if (setupRequired) return "First Login: Set Password";
    return "Login to LiftNet";
  };

  const panelSubtitle = () => {
    if (forcedChangeRequired) {
      return `This account is still using the password it was issued. Choose your own to finish signing in.`;
    }
    if (setupRequired) {
      return `Welcome ${email}. Set a password to finish activating your account.`;
    }
    return "Monitoring Offline Application";
  };

  const submitPassword = () => {
    if (forcedChangeRequired) {
      onClickChangeOwnPassword();
      return;
    }
    onClickSetInitialPassword();
  };

  const passwordSubmitDisabled =
    setupNewPassword === "" ||
    setupConfirmPassword === "" ||
    (setupRequired && setupToken.trim() === "");

  // The request error on screen, if any. The panel showing decides which of the three calls it is.
  const activeFailure = (): Failure | undefined => {
    if (forcedChangeRequired) {
      return changeIsError && changeError
        ? {
            error: changeError,
            path: OWN_CUSTOM_OFFLINE_PASSWORD_URL,
            flow: "Choose your own password",
          }
        : undefined;
    }
    if (setupRequired) {
      return setupIsError && setupError
        ? {
            error: setupError,
            path: OFFLINE_INITIAL_PASSWORD_URL,
            flow: "First login: set password",
          }
        : undefined;
    }
    return isError && error ? { error, path: LOGIN_OFFLINE_URL, flow: "Login" } : undefined;
  };

  const failure = activeFailure();
  const failureRequestUrl = failure ? joinUrl(apiBaseUrl, failure.path) : "";
  const failureMessage: LoginErrorMessage | undefined = failure
    ? describeLoginError(failure.error, failureRequestUrl, window.location.origin)
    : undefined;
  // A mistake in the form (client-side check) replaces a stale server message, as before.
  const notice: LoginErrorMessage | undefined =
    showPasswordPanel && setupClientError ? { headline: setupClientError } : failureMessage;

  const onClickGetLog = async () => {
    if (!failure) return;
    setIsPreparingLog(true);
    try {
      // Checked now, when support asks for it, not on every failed request.
      const probe = isConnectionFailure(failure.error)
        ? await probeServerReachability(failureRequestUrl, window.location.origin)
        : undefined;
      const generatedAt = new Date();
      await downloadLoginLogBundle({
        generatedAt,
        failedAt: failedAt ?? generatedAt,
        appVariant: process.env.REACT_APP_VERSION || DEFAULT_APP_VERSION,
        flow: failure.flow,
        method: "POST",
        requestUrl: failureRequestUrl,
        configKey: API_BASE_URL_KEY,
        attempts: loginAttempts(failure.error),
        error: failure.error,
        probe,
        environment: readBrowserEnvironment(),
      });
    } catch {
      message.error("Could not create the log file. Please try again.");
    } finally {
      setIsPreparingLog(false);
    }
  };

  const noticeBlock = notice ? (
    <div
      className="loginStyles__loginMainBox generalStyles__error"
      role="alert"
      data-testid="login-offline-error"
      style={{
        maxHeight: "calc(100% - 12px)",
        overflowY: "auto",
        marginBottom: "12px",
        padding: "12px 16px",
        border: "1px solid #FECACA",
        background: "#FFF1F2",
        lineHeight: 1.4,
      }}
    >
      <p style={{ margin: 0, fontWeight: 700 }}>{notice.headline}</p>
      {notice.hint && <p style={{ margin: "4px 0 0 0", fontSize: "13px" }}>{notice.hint}</p>}
    </div>
  ) : null;

  // Only for a request error; a form mistake has nothing for support to look at.
  const getLogButton =
    failureMessage && !(showPasswordPanel && setupClientError) ? (
      <Button
        className="buttonStyle__14 mt-3 generalStyles__width150px generalStyles__mlFix"
        data-testid="login-offline-get-log"
        loading={isPreparingLog}
        onClick={onClickGetLog}
      >
        Get log
      </Button>
    ) : null;

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <>
      {launchToDashboard ? (
        <>
          {window.location.replace(
            `/${process.env.REACT_APP_VERSION || DEFAULT_APP_VERSION}/monitoring`,
          )}
        </>
      ) : (
        <div className="loginStyles__body">
          <div className="loginStyles__loginSubBox1 d-none d-sm-block">
            <CustomAmplifyCarousel />
          </div>
          <div className="loginStyles__loginSubBox2">
            <Spin
              spinning={isLoading || setupIsLoading || changeIsLoading}
              wrapperClassName="loginStyle__spinContainer"
            >
              <div>
                <div className="loginStyles__100Container">
                  {/* The notice lives in the empty top third, above the title, so how long it is
                      can never push the form's buttons into the footer links. */}
                  <div
                    className="loginStyles__33Container"
                    style={{ display: "flex", alignItems: "flex-end", justifyContent: "center" }}
                  >
                    {noticeBlock}
                  </div>
                  <div className="loginStyles__33Container">
                    <div className="loginStyles__loginMainBox">
                      {/** DESCRIPTION */}
                      <div className=" d-none d-sm-block">
                        <p className="loginStyles__homeLogin">{panelTitle()}</p>
                        <p
                          className="loginStyles__homeLogin loginStyles__monitoringApplication"
                          style={{ marginTop: "-20px" }}
                        >
                          {panelSubtitle()}
                        </p>
                      </div>
                      {showPasswordPanel ? (
                        <>
                          {/* INPUT - SETUP TOKEN. Only the activation flow
                              needs one: a forced change is authenticated by the
                              token the login just issued. */}
                          {setupRequired && (
                            <>
                              <Input.Password
                                placeholder="Setup token (from administrator)"
                                prefix={
                                  <FontAwesomeIcon
                                    className="generalStyles__info generalStyles__mrFix"
                                    icon={faLock}
                                  />
                                }
                                onChange={(e) => {
                                  setSetupToken(e.target.value);
                                }}
                                value={setupToken}
                              />
                              <br />
                              <br />
                            </>
                          )}
                          {/* INPUT - NEW PASSWORD */}
                          <Input.Password
                            placeholder={`New password (min ${PASSWORD_MIN_LENGTH} chars, upper+lower+digit)`}
                            prefix={
                              <FontAwesomeIcon
                                className="generalStyles__info generalStyles__mrFix"
                                icon={faLock}
                              />
                            }
                            onChange={(e) => {
                              setSetupNewPassword(e.target.value);
                            }}
                            value={setupNewPassword}
                          />
                          <br />
                          <br />
                          {/* INPUT - CONFIRM PASSWORD */}
                          <Input.Password
                            placeholder="Confirm new password"
                            prefix={
                              <FontAwesomeIcon
                                className="generalStyles__info generalStyles__mrFix"
                                icon={faLock}
                              />
                            }
                            onChange={(e) => {
                              setSetupConfirmPassword(e.target.value);
                            }}
                            value={setupConfirmPassword}
                          />
                          <Button
                            className={`${
                              passwordSubmitDisabled ? "" : "buttonStyle__1 "
                            } mt-3 generalStyles__width150px`}
                            disabled={passwordSubmitDisabled}
                            onClick={submitPassword}
                          >
                            Set Password
                          </Button>
                          {getLogButton}
                        </>
                      ) : (
                        <>
                          {/* INPUT - EMAIL */}
                          <Input
                            placeholder="Email"
                            prefix={
                              <FontAwesomeIcon
                                icon={faEnvelope}
                                className="generalStyles__info generalStyles__mrFix"
                              />
                            }
                            onChange={(e) => {
                              setEmail(e.target.value.toLocaleLowerCase());
                            }}
                            value={email}
                          />
                          <br />
                          <br />
                          {/* INPUT - PASSWORD */}
                          <Input.Password
                            placeholder="Password"
                            prefix={
                              <FontAwesomeIcon
                                className="generalStyles__info generalStyles__mrFix"
                                icon={faLock}
                              />
                            }
                            onChange={(e) => {
                              setPassword(e.target.value);
                            }}
                            value={password}
                          />
                          <Button
                            className={`${
                              password === "" || email === "" ? "" : "buttonStyle__1 "
                            } mt-3 generalStyles__width150px`}
                            disabled={password === "" || email === ""}
                            onClick={onClickLogin}
                          >
                            Login
                          </Button>
                          {getLogButton}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="loginStyles__33Container">
                    <div
                      style={{
                        position: "absolute",
                        bottom: "10px",
                        right: "0px",
                        display: "flex",
                        fontSize: "12px",
                      }}
                    >
                      {/** ********************************* */}
                      {/** MSSA PAGE */}
                      <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={emptyFunctionDeclaration}
                        onClick={() => {
                          window.location.href = "/terms/mssa";
                        }}
                      >
                        <FontAwesomeIcon className="generalStyles__info" icon={faCircleInfo} />
                        <span className="generalStyles__mlFix generalStyles__mrFix25 loginStyles__termsAndConditionsLink">
                          Terms and Conditions
                        </span>
                      </div>
                      {/** ********************************* */}
                      {/** EULA PAGE */}
                      <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={emptyFunctionDeclaration}
                        onClick={() => {
                          window.location.href = "/terms/eula";
                        }}
                      >
                        <FontAwesomeIcon className="generalStyles__info" icon={faCircleInfo} />
                        <span className="generalStyles__mlFix generalStyles__mrFix25 loginStyles__termsAndConditionsLink">
                          End User License Agreement
                        </span>
                      </div>
                      {/** ********************************* */}
                      {/** SUPPORT POLICY PAGE */}
                      <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={emptyFunctionDeclaration}
                        onClick={() => {
                          window.location.href = "/terms/supportpolicy";
                        }}
                      >
                        <FontAwesomeIcon className="generalStyles__info" icon={faCircleInfo} />
                        <span className="generalStyles__mlFix generalStyles__mrFix25 loginStyles__termsAndConditionsLink">
                          Support Policy
                        </span>
                      </div>
                      <div />
                    </div>
                  </div>
                </div>
              </div>
            </Spin>
          </div>
        </div>
      )}
    </>
  );
};

export default LoginOfflinePage;
